import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Chip, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import orderFillType from 'common/constants/orderFillType';
import orderStatus from 'common/constants/orderStatus';
import { useGetPartnerAccountName } from 'common/utils';
import { orderSource } from 'enums/order.enum';
import { capitalize } from 'lodash';
import PropTypes from 'prop-types';

const orderStatusChipColor = (status) => {
  if (status === 'SHIPPED') return 'success';
  if (status === 'CANCELED') return 'error';
  return 'info';
};

const HeaderMeta = ({ label, children }) => (
  <Box sx={{ textAlign: 'right' }}>
    <Typography variant='caption' color='rgba(255,255,255,0.7)' display='block'>
      {label}
    </Typography>
    {children}
  </Box>
);

HeaderMeta.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const OrderHeader = ({ order, fulfillingPharmacyName }) => {
  const partnerAccountHandler = useGetPartnerAccountName();

  const getPartnerAccountName = (partnerAccountId) =>
    partnerAccountId && partnerAccountHandler[partnerAccountId]
      ? capitalize(partnerAccountHandler[partnerAccountId].partnerAccountName)
      : null;

  return (
    <Grid
      container
      sx={{ px: 3, py: 1, bgcolor: 'primary.light' }}
      justifyContent='space-between'
      alignItems='center'
    >
      {/* Left — status chips */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography component='h2' variant='h6' fontWeight='bold' color='white'>
          Order — {order.orderId}
        </Typography>
        {order.orderStatus && (
          <Chip
            label={orderStatus[order.orderStatus] || order.orderStatus}
            size='small'
            color={orderStatusChipColor(order.orderStatus)}
            sx={{ fontWeight: 600 }}
          />
        )}

        {order.fillType === 'FIRST_FILL' && (
          <Chip
            label={orderFillType.FIRST_FILL}
            size='small'
            color='success'
            sx={{ fontWeight: 600 }}
          />
        )}
        {order.fillType === 'REFILL' && (
          <Chip label={orderFillType.REFILL} size='small' color='info' sx={{ fontWeight: 600 }} />
        )}
        {order.reshipmentOf && (
          <Chip
            label={`Reshipment of ${order.reshipmentOf}`}
            size='small'
            color='info'
            sx={{ fontWeight: 600 }}
          />
        )}
        {/* isFillLater, not fillLaterOf: a whole-order move parks the order in place and leaves
            parent_order_id null, so the pointer below is absent even though the order is in Fill
            Later. Only a split off another order has a parent to name.

            POM-6320: left off once the status itself reads Fill Later, where the chip beside it
            says the same word and adds nothing. It still shows on a split child, which is OPEN
            and being filled now and carries the flag as its only sign of where it came from. */}
        {order.isFillLater && order.orderStatus !== 'FILL_LATER' && (
          <Chip label='Fill Later' size='small' color='info' sx={{ fontWeight: 600 }} />
        )}
        {order.fillLaterOf && order.fillLaterOf !== order.orderId && (
          <Chip
            label={`Split of ${order.fillLaterOf}`}
            size='small'
            color='info'
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Right — metadata fields + close */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {order.source != null && orderSource[order.source]?.sourceValue && (
          <>
            <HeaderMeta label='Source'>
              <Typography variant='body2' color='white' fontWeight={600}>
                {orderSource[order.source].sourceValue}
              </Typography>
            </HeaderMeta>
            <Divider
              orientation='vertical'
              flexItem
              sx={{ borderColor: 'rgba(255,255,255,0.3)' }}
            />
          </>
        )}

        {order.partner?.partnerName && (
          <>
            <HeaderMeta label='Partner'>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={order.partner.partnerUuid || ''} placement='bottom'>
                  <Typography variant='body2' color='white' fontWeight={600}>
                    {order.partner.partnerName}
                  </Typography>
                </Tooltip>
                {order.partner.partnerUuid && (
                  <Tooltip
                    title={`Copy partner ID: ${order.partner.partnerUuid}`}
                    placement='bottom'
                  >
                    <IconButton
                      size='small'
                      onClick={() => navigator.clipboard.writeText(order.partner.partnerUuid)}
                      sx={{ color: 'rgba(255,255,255,0.7)', p: 0.25 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </HeaderMeta>
            <Divider
              orientation='vertical'
              flexItem
              sx={{ borderColor: 'rgba(255,255,255,0.3)' }}
            />
          </>
        )}

        {order.partnerAccountId && (
          <>
            <HeaderMeta label='Partner Account'>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={order.partnerAccountId} placement='bottom'>
                  <Typography variant='body2' color='white' fontWeight={600}>
                    {getPartnerAccountName(order.partnerAccountId) || order.partnerAccountId}
                  </Typography>
                </Tooltip>
                <Tooltip
                  title={`Copy partner account ID: ${order.partnerAccountId}`}
                  placement='bottom'
                >
                  <IconButton
                    size='small'
                    onClick={() => navigator.clipboard.writeText(order.partnerAccountId)}
                    sx={{ color: 'rgba(255,255,255,0.7)', p: 0.25 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </HeaderMeta>
            <Divider
              orientation='vertical'
              flexItem
              sx={{ borderColor: 'rgba(255,255,255,0.3)' }}
            />
          </>
        )}

        {order.practice?.practiceName && (
          <>
            <HeaderMeta label='Practice'>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={order.practice.practiceId || ''} placement='bottom'>
                  <Typography variant='body2' color='white' fontWeight={600}>
                    {order.practice.practiceName}
                  </Typography>
                </Tooltip>
                {order.practice.practiceId && (
                  <Tooltip
                    title={`Copy practice ID: ${order.practice.practiceId}`}
                    placement='bottom'
                  >
                    <IconButton
                      size='small'
                      onClick={() => navigator.clipboard.writeText(order.practice.practiceId)}
                      sx={{ color: 'rgba(255,255,255,0.7)', p: 0.25 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </HeaderMeta>
            <Divider
              orientation='vertical'
              flexItem
              sx={{ borderColor: 'rgba(255,255,255,0.3)' }}
            />
          </>
        )}

        {fulfillingPharmacyName && (
          <>
            <HeaderMeta label='Fulfilling Pharmacy'>
              <Typography variant='body2' color='white' fontWeight={600}>
                {fulfillingPharmacyName}
              </Typography>
            </HeaderMeta>
            <Divider
              orientation='vertical'
              flexItem
              sx={{ borderColor: 'rgba(255,255,255,0.3)' }}
            />
          </>
        )}
      </Box>
    </Grid>
  );
};

OrderHeader.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.string,
    patientName: PropTypes.string,
    orderStatus: PropTypes.string,
    fillType: PropTypes.string,
    source: PropTypes.number,
    partner: PropTypes.shape({
      partnerName: PropTypes.string,
      partnerUuid: PropTypes.string,
    }),
    practice: PropTypes.shape({
      practiceName: PropTypes.string,
      practiceId: PropTypes.string,
    }),
    partnerAccountId: PropTypes.string,
    reshipmentOf: PropTypes.string,
    fillLaterOf: PropTypes.string,
    isFillLater: PropTypes.bool,
  }).isRequired,
  fulfillingPharmacyName: PropTypes.string,
};

OrderHeader.defaultProps = {
  fulfillingPharmacyName: null,
};

export default OrderHeader;
